const consentCookieName = "cookieConsent";
const legacyCookieName = "cookieAccepted";
const consentCategories = ["analytics", "marketing"];
const categoryCookies = {
  analytics: [/^_ga$/, /^_ga_/, /^_gid$/, /^_gat/],
  marketing: [/^_fbp$/, /^_fbc$/, /^_ttp$/, /^_tt_enable_cookie$/, /^ttcsid/],
};
const focusableSelector = "button:not([disabled]), input:not([disabled]), a[href]";

const activeCategories = new Set();
const consentChannel = "BroadcastChannel" in window ? new BroadcastChannel(consentCookieName) : null;
let settingsTrigger = null;

const getCookie = (name) => {
  const value = " " + document.cookie;
  const parts = value.split(" " + name + "=");
  return parts.length < 2 ? undefined : parts.pop().split(";").shift();
};

const setCookie = (name, value, expiryDays) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + expiryDays);
  document.cookie =
    name +
    "=" +
    value +
    ";expires=" +
    expires.toUTCString() +
    ";path=/;SameSite=Lax" +
    (location.protocol === "https:" ? ";secure" : "");
};

const readConsent = () => {
  const value = getCookie(consentCookieName);
  if (!value) {
    return null;
  }
  const consent = {};
  consentCategories.forEach((category) => {
    consent[category] = value.split("|").includes(category + ":1");
  });
  return consent;
};

const writeConsent = (consent) => {
  const value = consentCategories
    .map((category) => category + ":" + (consent[category] ? 1 : 0))
    .join("|");
  setCookie(consentCookieName, value, 182);
};

const removeCookies = (patterns) => {
  const hostParts = location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < hostParts.length - 1; i++) {
    domains.push(";domain=." + hostParts.slice(i).join("."));
  }
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!patterns.some((pattern) => pattern.test(name))) {
      return;
    }
    domains.forEach((domain) => {
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/" + domain;
    });
  });
};

const activateScripts = (consent) => {
  document
    .querySelectorAll('script[type="text/plain"][data-cookie-category]')
    .forEach((placeholder) => {
      const category = placeholder.dataset.cookieCategory;
      if (!consent[category]) {
        return;
      }
      const script = document.createElement("script");
      [].slice.call(placeholder.attributes).forEach((attribute) => {
        if (attribute.name !== "type" && attribute.name !== "data-cookie-category") {
          script.setAttribute(attribute.name, attribute.value);
        }
      });
      script.text = placeholder.text;
      placeholder.replaceWith(script);
      activeCategories.add(category);
    });
};

const updatePage = (consent) => {
  if (consentCategories.some((category) => activeCategories.has(category) && !consent[category])) {
    location.reload();
    return;
  }
  activateScripts(consent);
};

const getBanner = () => document.querySelector("[data-cookie-banner]");
const getSettings = () => document.querySelector("[data-cookie-settings]");

const updateReopenButton = () => {
  const button = document.querySelector("[data-cookie-reopen]");
  if (button) {
    button.hidden = !readConsent();
  }
};

const showBanner = () => {
  const banner = getBanner();
  if (banner) {
    banner.classList.add(banner.dataset.cookieVisibleClass);
  }
};

const hideBanner = () => {
  const banner = getBanner();
  if (banner) {
    banner.classList.remove(banner.dataset.cookieVisibleClass);
  }
};

const isSettingsOpen = () => {
  const settings = getSettings();
  return !!settings && !settings.hidden;
};

const hideSettings = () => {
  const settings = getSettings();
  if (!settings || settings.hidden) {
    return;
  }
  settings.hidden = true;
  if (settingsTrigger && document.contains(settingsTrigger)) {
    settingsTrigger.focus();
  }
  settingsTrigger = null;
};

const openSettings = (trigger) => {
  const settings = getSettings();
  if (!settings) {
    return;
  }
  const consent = readConsent() || {};
  consentCategories.forEach((category) => {
    const checkbox = settings.querySelector('[data-cookie-category-toggle="' + category + '"]');
    if (checkbox) {
      checkbox.checked = !!consent[category];
    }
  });
  settingsTrigger = trigger;
  hideBanner();
  settings.hidden = false;
  settings.querySelector(focusableSelector).focus();
};

const closeSettings = () => {
  if (!readConsent()) {
    showBanner();
  }
  hideSettings();
};

const trapFocus = (event) => {
  const focusable = [].slice.call(getSettings().querySelectorAll(focusableSelector));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const applyConsent = (consent) => {
  writeConsent(consent);
  hideBanner();
  hideSettings();
  updateReopenButton();
  removeCookies([new RegExp("^" + legacyCookieName + "$")]);
  consentCategories
    .filter((category) => !consent[category])
    .forEach((category) => removeCookies(categoryCookies[category]));
  if (consentChannel) {
    consentChannel.postMessage(consent);
  }
  updatePage(consent);
};

const allCategories = (value) => {
  const consent = {};
  consentCategories.forEach((category) => {
    consent[category] = value;
  });
  return consent;
};

const selectedCategories = () => {
  const settings = getSettings();
  const consent = {};
  consentCategories.forEach((category) => {
    const checkbox = settings && settings.querySelector('[data-cookie-category-toggle="' + category + '"]');
    consent[category] = !!(checkbox && checkbox.checked);
  });
  return consent;
};

const actions = {
  "accept-all": () => applyConsent(allCategories(true)),
  "reject-all": () => applyConsent(allCategories(false)),
  save: () => applyConsent(selectedCategories()),
  settings: openSettings,
  "open-settings": openSettings,
  "close-settings": closeSettings,
  close: hideBanner,
};

document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-cookie-action]");
  if (!trigger || !actions[trigger.dataset.cookieAction]) {
    return;
  }
  event.preventDefault();
  actions[trigger.dataset.cookieAction](trigger);
});

document.addEventListener("keydown", (event) => {
  if (!isSettingsOpen()) {
    return;
  }
  if (event.key === "Escape") {
    closeSettings();
  } else if (event.key === "Tab") {
    trapFocus(event);
  }
});

if (consentChannel) {
  consentChannel.addEventListener("message", (event) => {
    hideBanner();
    hideSettings();
    updateReopenButton();
    updatePage(event.data);
  });
}

document.addEventListener("DOMContentLoaded", () => {
  const consent = readConsent();
  updateReopenButton();
  if (consent) {
    activateScripts(consent);
    return;
  }
  setTimeout(() => {
    if (!readConsent() && !isSettingsOpen()) {
      showBanner();
    }
  }, 1000);
});
